
import React, { useState, useEffect } from 'react';
import { SimulationConfig, MelodyInstrument } from './types';
import { SectionLabel } from './SectionLabel';
import { Toggle } from './Toggle';
import { SmartInput } from './SmartInput';
import { ControlGroup } from './ControlGroup';
import { AUDIO } from './services/AudioService';
import { CloudService } from './services/CloudService';
import { PRESET_LIBRARY } from './presets';

// Subcomponents
import { VisualSlicer } from './components/settings/audio/VisualSlicer';
import { PresetLibrary } from './components/settings/audio/PresetLibrary';
import { AudioOverrides } from './components/settings/audio/AudioOverrides';
import { ManualRack } from './components/settings/audio/ManualRack';

import { motion } from 'motion/react';

interface AudioPostProcessingSectionProps {
  config: SimulationConfig;
  setConfig: React.Dispatch<React.SetStateAction<SimulationConfig>>;
  cloudSaves?: any[];
}

export const AudioPostProcessingSection: React.FC<AudioPostProcessingSectionProps> = ({ config, setConfig, cloudSaves = [] }) => {
  const [rackTarget, setRackTarget] = useState<'MELODY' | 'FX'>('MELODY');
  const [isSongUploading, setIsSongUploading] = useState(false);
  const [cloudSongs, setCloudSongs] = useState<any[]>([]);

  useEffect(() => {
      CloudService.getSongs().then(setCloudSongs);
  }, []);

  const smartRandomize = () => {
    const preset = PRESET_LIBRARY[Math.floor(Math.random() * PRESET_LIBRARY.length)];
    setConfig(p => ({
        ...p,
        ...preset.settings,
        audioReverbLevel: Math.max(0, Math.min(1, (preset.settings.audioReverbLevel || 0) + (Math.random() - 0.5) * 0.2)),
        audioDelayTime: Math.max(0.05, Math.min(1.0, (preset.settings.audioDelayTime || 0.3) + (Math.random() - 0.5) * 0.2)),
    }));
  };

  const hardReset = () => {
      setConfig(p => ({
          ...p,
          audioReverbLevel: 0,
          audioDistortionLevel: 0,
          audioDelayTime: 0.3,
          audioDelayFeedback: 0,
          audioEcho: false,
          fxReverbLevel: 0,
          fxDistortionLevel: 0,
          fxEcho: false,
          audioLowPassFreq: 20000,
          audioLowPassResonance: 1.0,
          melodyInstrument: 'GRAND_PIANO',
          ballCollisionSoundType: 'MELODY',
          audioPitchShift: 0,
          audioCompThreshold: -12,
          audioCompRatio: 4,
          universalForceInstrument: false
      }));
  };

  const handleSongUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
      const file = e.target.files?.[0];
      if (!file) return;
      setIsSongUploading(true);
      
      const reader = new FileReader();
      reader.onload = async (evt) => {
          const dataUrl = evt.target?.result as string;
          if (dataUrl) {
              await CloudService.registerSong(file.name.replace('.mp3', ''), dataUrl);
              CloudService.getSongs().then(setCloudSongs);
          }
      };
      reader.readAsDataURL(file);

      const duration = await AUDIO.uploadSongForSlicing(file);
      setIsSongUploading(false);
      
      if (duration > 0) {
          setConfig(p => ({
              ...p,
              songTotalDuration: duration,
              ballCollisionSoundType: 'SONG_SLICE',
              melodyVolume: 1.0,
              audioEcho: false,
              audioReverbLevel: 0.1
          }));
          alert(`Song Loaded & Saved to Cloud (${duration.toFixed(1)}s)!`);
      } else {
          alert("Failed to load song. Please try a valid MP3/WAV.");
      }
  };

  const loadCloudSong = async (song: any) => {
      if (!song.data) return;
      setIsSongUploading(true);
      
      const res = await fetch(song.data);
      const blob = await res.blob();
      const file = new File([blob], song.name, { type: 'audio/mp3' });
      
      const duration = await AUDIO.uploadSongForSlicing(file);
      setIsSongUploading(false);

      if (duration > 0) {
        setConfig(p => ({
            ...p,
            songTotalDuration: duration,
            ballCollisionSoundType: 'SONG_SLICE',
            melodyVolume: 1.0,
            audioEcho: false,
            audioReverbLevel: 0.1
        }));
      }
  };

  const isM = rackTarget === 'MELODY';
  const instruments: MelodyInstrument[] = [
      'GRAND_PIANO', 'BRIGHT_PIANO', 'ELECTRIC_PIANO', 'HONKY_TONK', 'HARPSICHORD', 'CLAVINET',
      'CELESTA', 'GLOCKENSPIEL', 'MUSIC_BOX', 'VIBRAPHONE', 'MARIMBA', 'XYLOPHONE', 'TUBULAR_BELLS', 'DULCIMER',
      'DRAWBAR_ORGAN', 'PERCUSSIVE_ORGAN', 'ROCK_ORGAN', 'CHURCH_ORGAN', 'REED_ORGAN', 'ACCORDION', 'HARMONICA', 'TANGO_ACCORDION',
      'NYLON_GUITAR', 'STEEL_GUITAR', 'JAZZ_GUITAR', 'CLEAN_GUITAR', 'MUTED_GUITAR', 'OVERDRIVEN_GUITAR', 'DISTORTION_GUITAR', 'GUITAR_HARMONICS',
      'ACOUSTIC_BASS', 'FINGER_BASS', 'PICK_BASS', 'FRETLESS_BASS', 'SLAP_BASS_1', 'SLAP_BASS_2', 'SYNTH_BASS_1', 'SYNTH_BASS_2', 'SYNTH_BASS_3', 'SYNTH_BASS_4',
      'VIOLIN', 'VIOLA', 'CELLO', 'CONTRABASS', 'TREMOLO_STRINGS', 'PIZZICATO_STRINGS', 'ORCHESTRAL_HARP', 'TIMPANI',
      'STRING_ENSEMBLE_1', 'STRING_ENSEMBLE_2', 'SYNTH_STRINGS_1', 'SYNTH_STRINGS_2', 'CHOIR_AAHS', 'VOICE_OOHS', 'SYNTH_VOICE', 'ORCHESTRA_HIT', 'ORCHESTRA_HIT_3',
      'TRUMPET', 'TROMBONE', 'TUBA', 'MUTED_TRUMPET', 'FRENCH_HORN', 'BRASS_SECTION', 'SYNTH_BRASS_1', 'SYNTH_BRASS_2',
      'SOPRANO_SAX', 'ALTO_SAX', 'TENOR_SAX', 'BARITONE_SAX', 'OBOE', 'ENGLISH_HORN', 'BASSOON', 'CLARINET',
      'PICCOLO', 'FLUTE', 'RECORDER', 'PAN_FLUTE', 'BLOWN_BOTTLE', 'SHAKUHACHI', 'WHISTLE', 'OCARINA',
      'SQUARE_LEAD', 'SAWTOOTH_LEAD', 'CALLIOPE_LEAD', 'CHIFF_LEAD', 'CHARANG_LEAD', 'VOICE_LEAD', 'FIFTHS_LEAD', 'BASS_LEAD', '8BIT_LEAD', 'SYNTH_PLUCK', 'SYNTH_LEAD_3', 'SYNTH_LEAD_4',
      'NEW_AGE_PAD', 'WARM_PAD', 'POLYSYNTH_PAD', 'CHOIR_PAD', 'BOWED_PAD', 'METALLIC_PAD', 'HALO_PAD', 'SWEEP_PAD', 'SPACE_VOICE', 'ALIEN_DRONE',
      'RAIN_FX', 'SOUNDTRACK_FX', 'CRYSTAL_FX', 'ATMOSPHERE_FX', 'BRIGHTNESS_FX', 'GOBLINS_FX', 'ECHOES_FX', 'SCI_FI_FX', 'ETHEREAL_CHIME', 'THUNDER_FX', 'WIND_FX', 'WATER_FX', 'BIRD_TWEET', 'TELEPHONE_RING', 'HELICOPTER', 'APPLAUSE', 'GUNSHOT',
      'SITAR', 'BANJO', 'SHAMISEN', 'KOTO', 'KALIMBA', 'BAGPIPE', 'FIDDLE', 'SHANAI',
      'TINKLE_BELL', 'AGOGO', 'STEEL_DRUMS', 'WOODBLOCK', 'TAIKO_DRUM', 'MELODIC_TOM', 'SYNTH_DRUM', 'REVERSE_CYMBAL', 'PURE_SINE', 'CHURCH_BELL', 'HAND_BELL'
  ];

  return (
    <div className={`p-5 rounded-3xl border ${config.darkMode ? 'bg-emerald-500/5 border-emerald-500/10' : 'bg-emerald-50 border-emerald-100'} flex flex-col gap-4 shadow-sm mb-4`}>
      <SectionLabel icon="fa-wand-magic-sparkles" darkMode={config.darkMode}>Studio Engine</SectionLabel>
      
      <div className="grid grid-cols-2 gap-3 mb-2">
        <button onClick={smartRandomize} className="py-3 bg-yellow-600 text-white rounded-2xl font-black text-[10px] uppercase hover:scale-[1.02] active:scale-95 transition-all shadow-lg shadow-yellow-500/20">
            <i className="fa-solid fa-dice mr-2"></i> Smart Random
        </button>
        <button onClick={hardReset} className="py-3 bg-red-600 text-white rounded-2xl font-black text-[10px] uppercase hover:scale-[1.02] active:scale-95 transition-all shadow-lg shadow-red-500/20">
            <i className="fa-solid fa-power-off mr-2"></i> Clean Reset
        </button>
      </div>

      <div className={`p-5 rounded-2xl bg-gradient-to-br ${config.darkMode ? 'from-blue-600/10 to-purple-600/10 border-blue-500/20' : 'from-blue-50 to-purple-50 border-blue-100 shadow-inner'} border mb-2`}>
          <div className="flex justify-between items-center mb-4">
              <span className="text-[10px] font-bold uppercase opacity-80 text-blue-500"><i className="fa-solid fa-scissors mr-2"></i> Real Song Slicer</span>
              {config.ballCollisionSoundType === 'SONG_SLICE' && <span className="text-[9px] bg-green-500 text-white px-3 py-0.5 rounded-full font-black tracking-wider shadow-sm animate-pulse">ACTIVE</span>}
          </div>
          
          <label className={`w-full py-6 rounded-2xl border-2 border-dashed flex flex-col items-center justify-center gap-2 cursor-pointer transition-all ${config.darkMode ? 'border-white/10 hover:bg-white/5' : 'border-neutral-200 hover:bg-white bg-white/50 shadow-sm'} mb-4`}>
              <i className={`fa-solid ${isSongUploading ? 'fa-spinner fa-spin' : 'fa-upload'} text-xl opacity-40`}></i>
              <span className="text-[10px] font-black uppercase opacity-60 tracking-wider text-center">{isSongUploading ? 'Analyzing Waveform...' : 'Drop MP3 here or Click to Browse'}</span>
              <input type="file" accept="audio/*" onChange={handleSongUpload} className="hidden" />
          </label>

          {cloudSongs.length > 0 && (
              <div className="mb-6">
                  <span className="text-[10px] font-bold uppercase opacity-60 mb-1.5 px-1 truncate">Remote Session Memory</span>
                  <div className={`p-1 rounded-2xl border ${config.darkMode ? 'bg-black/20 border-white/5 shadow-inner' : 'bg-white/50 border-neutral-100 shadow-sm'}`}>
                    <select 
                        onChange={(e) => {
                            const song = cloudSongs.find(s => s.id === e.target.value);
                            if (song) loadCloudSong(song);
                        }}
                        className={`w-full px-3 py-2 rounded-xl text-[11px] font-black outline-none cursor-pointer ${config.darkMode ? 'bg-transparent text-white' : 'bg-transparent text-neutral-800'}`}
                    >
                        <option value="" className="bg-neutral-900 text-white">Select session song...</option>
                        {cloudSongs.map(s => <option key={s.id} value={s.id} className="bg-neutral-900 text-white">{s.name}</option>)}
                    </select>
                  </div>
              </div>
          )}

          <div className="flex flex-col gap-4">
             <div className="flex flex-col gap-2.5">
                <div className="flex justify-between items-center px-1">
                    <span className="text-[10px] font-bold uppercase opacity-60 text-blue-400">Visual Timeline Slicer</span>
                    <button 
                        onClick={() => setConfig(p => ({...p, songSliceSequence: []}))}
                        className="text-[9px] font-black uppercase text-red-500/60 hover:text-red-500 transition-colors"
                    >
                        Clear Slices
                    </button>
                </div>
                
                <div className={`p-1 rounded-2xl border ${config.darkMode ? 'bg-black/40 border-white/5 shadow-inner' : 'bg-white border-neutral-100 shadow-sm'}`}>
                    <VisualSlicer config={config} setConfig={setConfig} />
                </div>
                <p className="technical-label !normal-case !opacity-30 px-1 !text-[9px] !leading-snug italic">
                    Drag the bounds to isolate melody chunks. These will trigger in sequence on each bounce.
                </p>
             </div>

             <div className="h-px bg-white/5 my-1" />
             
             <SmartInput 
                label="Step Length (sec)" 
                settingKey="songSliceDuration" 
                min={0.01} max={2.0} step={0.01} 
                value={config.songSliceDuration || 0.15} 
                onChange={v => setConfig(p => ({...p, songSliceDuration: isNaN(v) ? 0.15 : v}))} 
                darkMode={config.darkMode} 
             />
             
             <ControlGroup label="Continuous Flow" settingKey="songSliceContinuousMode" darkMode={config.darkMode}>
                <Toggle active={config.songSliceContinuousMode} onClick={() => setConfig(p => ({...p, songSliceContinuousMode: !p.songSliceContinuousMode}))} color="bg-blue-600" />
             </ControlGroup>

             <div className="h-px bg-white/5 my-1" />
             <ControlGroup label="Mute Studio Sync" settingKey="audioMutedInSlicer" darkMode={config.darkMode}>
                <Toggle active={config.audioMutedInSlicer} onClick={() => setConfig(p => ({...p, audioMutedInSlicer: !p.audioMutedInSlicer}))} color="bg-red-600" />
             </ControlGroup>

             <div className="h-px bg-white/5 my-1" />
             <ControlGroup label="Bounce Acceleration" settingKey="songSpeedIncreaseEnabled" darkMode={config.darkMode}>
                <Toggle active={config.songSpeedIncreaseEnabled} onClick={() => setConfig(p => ({...p, songSpeedIncreaseEnabled: !p.songSpeedIncreaseEnabled}))} color="bg-orange-600" />
             </ControlGroup>
             {config.songSpeedIncreaseEnabled && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-1">
                    <SmartInput label="Limiter Multiplier" settingKey="songSpeedMax" min={1.0} max={4.0} step={0.1} value={config.songSpeedMax || 2.0} onChange={v => setConfig(p => ({...p, songSpeedMax: v}))} darkMode={config.darkMode} />
                </motion.div>
             )}

             <div className="h-px bg-white/5 my-1" />
             <ControlGroup label="Autoreset (Safety)" settingKey="songResetEnabled" darkMode={config.darkMode}>
                <Toggle active={config.songResetEnabled} onClick={() => setConfig(p => ({...p, songResetEnabled: !p.songResetEnabled}))} color="bg-zinc-600" />
             </ControlGroup>
             {config.songResetEnabled && (
                <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mt-1">
                    <SmartInput label="Idle Timeout (sec)" settingKey="songResetTimer" min={0.1} max={10.0} step={0.1} value={config.songResetTimer || 1.0} onChange={v => setConfig(p => ({...p, songResetTimer: v}))} darkMode={config.darkMode} />
                </motion.div>
             )}
          </div>
      </div>

      <SectionLabel icon="fa-sliders" darkMode={config.darkMode}>Studio Tuning</SectionLabel>
      
      <div className={`flex flex-col gap-6 p-1 rounded-3xl ${config.darkMode ? '' : ''}`}>
        <div className="flex flex-col gap-6">
            <div className="flex flex-col gap-2">
                <span className="text-[10px] font-bold uppercase opacity-60 mb-2 px-1">Global Overrides</span>
                <AudioOverrides config={config} setConfig={setConfig} instruments={instruments} />
            </div>

            <div className="flex flex-col gap-2">
                <span className="text-[10px] font-bold uppercase opacity-60 mb-2 px-1">Logic Pattern Rack</span>
                <div className={`grid grid-cols-2 gap-1.5 p-1.5 rounded-2xl border ${config.darkMode ? 'bg-black/20 border-white/5 shadow-inner' : 'bg-neutral-100 border-neutral-200'}`}>
                    <button onClick={() => setRackTarget('MELODY')} className={`text-[10px] font-black py-2.5 rounded-xl transition-all uppercase tracking-wider ${isM ? 'bg-emerald-600 text-white shadow-lg shadow-emerald-600/20' : 'text-neutral-500'}`}>Melody Core</button>
                    <button onClick={() => setRackTarget('FX')} className={`text-[10px] font-black py-2.5 rounded-xl transition-all uppercase tracking-wider ${!isM ? 'bg-indigo-600 text-white shadow-lg shadow-indigo-600/20' : 'text-neutral-500'}`}>Collision FX</button>
                </div>
                
                <div className={`p-4 rounded-3xl border ${config.darkMode ? 'bg-black/20 border-white/5 shadow-inner' : 'bg-white border-neutral-100 shadow-sm'}`}>
                    <ManualRack config={config} setConfig={setConfig} isM={isM} instruments={instruments} />
                </div>
            </div>

            <div className="flex flex-col gap-3">
                <span className="text-[10px] font-bold uppercase opacity-60 mb-1 px-1">Advanced Processing</span>
                
                <div className={`p-4 rounded-3xl border ${config.darkMode ? 'bg-black/20 border-white/5' : 'bg-white border-neutral-100 shadow-sm'} flex flex-col gap-4`}>
                    <ControlGroup label="Audio Cutoff Limiter" settingKey="audioCutoffEnabled" darkMode={config.darkMode}>
                        <Toggle active={config.audioCutoffEnabled} onClick={() => setConfig(p => ({...p, audioCutoffEnabled: !p.audioCutoffEnabled}))} color="bg-red-500" />
                    </ControlGroup>
                    
                    <ControlGroup label="Compound Harmony" settingKey="audioCompoundMode" darkMode={config.darkMode}>
                        <Toggle active={config.audioCompoundMode} onClick={() => setConfig(p => ({...p, audioCompoundMode: !p.audioCompoundMode}))} color="bg-purple-600" />
                    </ControlGroup>

                    <div className="h-px bg-white/5" />

                    <ControlGroup label="Velocity Slide Integration" settingKey="audioSlidingEnabled" darkMode={config.darkMode}>
                        <Toggle active={config.audioSlidingEnabled} onClick={() => setConfig(p => ({...p, audioSlidingEnabled: !p.audioSlidingEnabled}))} color="bg-orange-600" />
                    </ControlGroup>
                    {config.audioSlidingEnabled && (
                        <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className={`flex flex-col gap-4 p-4 rounded-2xl border ${config.darkMode ? 'bg-black/40 border-white/5 shadow-inner' : 'bg-neutral-50 border-neutral-100 shadow-inner'}`}>
                            <SmartInput label="Slide Volume" settingKey="audioSlidingVolume" min={0} max={1} step={0.01} value={config.audioSlidingVolume} onChange={v => setConfig(p => ({...p, audioSlidingVolume: v}))} darkMode={config.darkMode} />
                            <SmartInput label="Slide Pitch" settingKey="audioSlidingPitch" min={0.1} max={2} step={0.01} value={config.audioSlidingPitch} onChange={v => setConfig(p => ({...p, audioSlidingPitch: v}))} darkMode={config.darkMode} />
                            <SmartInput label="Max Velocity Ref" settingKey="audioSlidingMaxSpeed" min={1} max={200} value={config.audioSlidingMaxSpeed} onChange={v => setConfig(p => ({...p, audioSlidingMaxSpeed: v}))} darkMode={config.darkMode} />
                        </motion.div>
                    )}
                </div>
            </div>
        </div>
      </div>

      <SectionLabel icon="fa-stream" darkMode={config.darkMode}>Cloud-Synced Factory Presets</SectionLabel>
      <div className={`p-4 rounded-3xl border ${config.darkMode ? 'bg-black/20 border-white/5' : 'bg-neutral-50 border-neutral-200'} shadow-inner`}>
        <PresetLibrary config={config} setConfig={setConfig} cloudSaves={cloudSaves} />
      </div>
    </div>
  );
};
